<template>
  <div class="page" v-if="can('article.store') || can('article.edit')">
    <!-- PAGE TITLE -->
    <h2 class="accent--text">
      <v-icon color="primary" class="ml-3" large>mdi-articlespaper</v-icon>
      إدارة المواد
      <v-icon color="primary" class="mx-1">mdi-chevron-left</v-icon>
      <span class="grey--text text--darken-1">{{ editMode ? record.title_text : 'اضافة' }}</span>
      <back-button />
    </h2>

    <hr class="mt-5 mb-9 header-divider" />

    <v-progress-linear
      v-if="fetchingRecord"
      color="primary"
      indeterminate
      rounded
      height="6"
      class="mb-4"
    ></v-progress-linear>

    <!-- BASIC INFO -->
    <v-card class="white elevation-1">
      <v-card-title class="grey lighten-4 primary--text">
        <v-icon class="ml-2">mdi-card-text-outline</v-icon>
        البيانات الأساسية
      </v-card-title>
      <v-card-text class="pa-6 mb-8">
        <v-textarea
          dense
          clearable
          rows="3"
          outlined
          label="عنوان المادة"
          v-model="form.title"
          :errors="errors.title"
        />

        <!-- PDF UPLOADER -->
        <file-uploader-input
          ref="pdfUploader"
          label="رفع ملف pdf للمادة"
          @input="form.pdf_download = $event"
        />

        <hr class="mt-0 mb-4 light-divider" />

        <v-row>
          <v-col cols="12" md="6" class="pb-0">
            <author-input
              :record="record"
              @input="form.author_ids = $event"
              :errors="errors.author_ids"
            />
          </v-col>

          <v-col cols="12" md="6" class="pb-0">
            <translator-input
              :record="record"
              @input="form.translator_id = $event"
              :errors="errors.translator_id"
            />
          </v-col>
        </v-row>

        <hr class="mt-0 mb-7 light-divider" />

        <v-file-input
          dense
          outlined
          label="صورة المادة"
          prepend-icon="mdi-image"
          accept="image/*"
          :loading="formLoading"
          v-model="form.caption"
          :error-messages="errors.caption"
        ></v-file-input>

        <editor
          label="النبذة"
          :value="form.summary"
          @input="form.summary = $event"
          :errors="errors.summary"
        />

        <editor
          label="المحتوي"
          :value="form.details"
          @input="form.details = $event"
          :errors="errors.details"
        />

        <v-combobox
          dense
          prepend-icon="mdi-key"
          outlined
          multiple
          clearable
          v-model="form.keywords_array"
          label="الكلمات المفتاحية"
          small-chips
        ></v-combobox>

        <sub-sections-input
          :record="record"
          @input="form.sub_section_id = $event"
          :errors="errors"
        />

        <tags-input
          ref="tagsInput"
          :record="record"
          @input="form.tags = $event"
        />

      </v-card-text>
    </v-card>

    <!-- EXISTENCE ATTACHMENTS -->
    <v-card v-if="editMode" class="white elevation-1">
      <v-card-title class="grey lighten-4 primary--text">
        <v-icon class="ml-2">mdi-paperclip</v-icon>
        المرفقات الموجودة
      </v-card-title>
      <v-card-text class="pa-6 mb-8">
        <attachments
          :record="record"
          :deleteMode="true"
          @update="fetchRecord(record.id)"
        />
      </v-card-text>
    </v-card>

    <!-- ATTACHMENTS UPLOADER -->
    <attachments-uploader ref="attachmentsUploader" />

    <!-- SUBMIT -->
    <div class="d-flex">
      <v-switch
        flat
        color="secondary"
        class="mt-0 mr-3"
        v-model="form.published"
        hide-details
        :false-value="0"
        :true-value="1"
        label="نشر"
      ></v-switch>

      <v-switch
        flat
        color="success"
        class="mt-0 mr-4"
        v-model="form.is_recommended"
        hide-details
        :false-value="0"
        :true-value="1"
        label="ترشيح المادة"
      ></v-switch>

      <v-spacer />
      <v-btn
        large
        icon
        color="info"
        class="mx-2"
        :href="previewLink"
        target="_blank"
      >
        <v-icon>mdi-eye</v-icon>
      </v-btn>

      <v-btn
        large
        elevation="1"
        color="primary"
        :loading="formLoading"
        @click="editMode ? update(form) : create(form)"
      >
        حفظ
      </v-btn>
    </div>
  </div>
</template>


<script>
import { mapState, mapMutations } from 'vuex';
import Editor from '@/components/Inputs/Editor';
import Attachments from '@/components/Attachment/List';
import AttachmentsUploader from '@/components/Attachment/Uploader';
import TagsInput from '@/components/Inputs/Tags';
import AuthorInput from '@/components/Inputs/Author';
import FileUploaderInput from '@/components/Inputs/FileUploader';
import TranslatorInput from '@/components/Inputs/Translator';
import SubSectionsInput from '@/components/Inputs/SubSections';

export default {
  name: 'record-form',
  components: {
      Editor,
      Attachments,
      AttachmentsUploader,
      TagsInput,
      AuthorInput,
      TranslatorInput,
      SubSectionsInput,
      FileUploaderInput
  },

  data: function () {
    return {
      errors: {},
      formLoading: false,
      form: { published: 0 },

      editMode: false,
      fetchingRecord: false,

      previewLink: ''
    };
  },


  created() {
    const recordId = this.$route.params.id;

    if (recordId != 0) {
      this.fetchRecord(recordId);
      this.previewLink = `${process.env.VUE_APP_WEBSITE_BASE_URL}/guest-admin/articles-unpublished?article_id=${recordId}`;
    }
  },


  computed: {
    ...mapState('articles', { record: (state) => state.currentRecord }),
  },


  watch: {
    record(form) {
      if (this.form.id) return;

      this.form = {
        id: form.id,
        title: form.title_text,
        author_ids: form.authors.map((author) => author.id),
        translator_id: form.translator_id,
        summary: form.summary,
        details: form.details,
        published: form.published,
        keywords_array: form.keywords ? form.keywords.split(',') : null,
        is_recommended: form.is_recommended,
        sub_section_id: form.sub_section_id,
        tags: form.tags
      };
    },
  },


  methods: {
    fetchRecord(recordId) {
      this.fetchingRecord = true;

      this.$store.dispatch('articles/fetchRecord', { id: recordId })
        .then(() => {
          this.editMode = true;
        })
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر جلب البيانات, يرجي التأكد من حالة الشبكة',
            color: 'accent'
          });
        })
        .finally(() => {
          this.fetchingRecord = false;
        });
    },

    async create(form) {
      this.formLoading = true;

      try {
        // upload the attahments and get array of its ids
        form.attachments = await this.$refs.attachmentsUploader.submit();

        // submit the tags and get array of its ids
        form.tags = await this.$refs.tagsInput.submit();
      }
      catch (e) {
        this.formLoading = false;
        return;
      }

      // prepare the record keywords
      if (Array.isArray(form.keywords_array)) {
        form.keywords = form.keywords_array.join(',');
      }

      this.$store
        .dispatch('articles/create', { form })
        .then((response) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تم إضافة مادة بنجاح',
            color: 'success',
          });
          this.$router.push(`/categories/${response.data.record.category.id}/articles`);
        })
        .catch((error) => {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
          }
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر إضافة مادة',
            color: 'accent',
          });
        })
        .finally(() => {
          this.formLoading = false;
        });
    },

    async update(form) {
      this.formLoading = true;

      try {
        // upload the attahments and get array of its ids
        form.attachments = await this.$refs.attachmentsUploader.submit();

        // submit the tags and get array of its ids
        form.tags = await this.$refs.tagsInput.submit();
      }
      catch (e) {
        this.formLoading = false;
        return;
      }

      // prepare the record keywords
      if (Array.isArray(form.keywords_array)) {
        form.keywords = form.keywords_array.join(',');
      }

      this.$store
        .dispatch('articles/update', { form })
        .then((response) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تم تعديل المادة بنجاح',
            color: 'success',
          });

          // this.$router.push(`/categories/${response.data.record.category.id}/articles`);
        })
        .catch((error) => {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
          }
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر تعديل المادة',
            color: 'accent',
          });
        })
        .finally(() => {
          this.formLoading = false;
        });
    }
  },
};
</script>
